import { apiRequest } from "./api";

// Maps a backend FilingSummaryResponse onto the My Filings table row shape.
// status is TaxReturn's own workflow state (new/half/full/submitted) - it
// never advances past "submitted" server-side, since the real IRS outcome is
// decided asynchronously by the scheduler and written to efile_queue_2290
// instead. queueStatus/submissionStatus surface that row (null until the
// return is actually enqueued for transmission) - see MyFilings.jsx's
// getMergedStatus for how the two are combined into what's actually shown.
function fromFilingSummary(r) {
  return {
    id: r.id,
    businessId: r.businessId,
    businessName: r.businessName || "",
    ein: r.ein || "",
    year: r.taxYear || "",
    formTypeCd: r.formTypeCd || "",
    status: r.status,
    balanceDue: r.balanceDueAmt != null ? Number(r.balanceDueAmt) : null,
    submissionId: r.submissionId || "",
    queueStatus: r.queueStatus || "",
    submissionStatus: r.submissionStatus || "",
    schedule1Available: !!r.schedule1Available,
    // IRS acknowledgement ReceiptId - what the filer quotes to the IRS as proof
    // the return was received. Written by the scheduler when the ack arrives, so
    // it is empty until queueStatus reaches ACK_RECEIVED.
    receiptId: r.receiptId || "",
    // The filer's saved IRS tax-payment method (efw/eftps/card, Pay step) - drives
    // the "pay the IRS now" deep link once accepted (see paymentMethods.js).
    paymentKind: r.paymentKind || "",
    createdAt: r.createdAt,
  };
}

// GET /returns?search=&status=&from=&to=&page=&size= -> every return the
// caller owns, across every business, newest first. All filters are
// optional - omitted ones are left out of the query string entirely so the
// backend's own defaults (status "all", page 0, size 10) apply.
export async function listFilings({ search, status, from, to, page = 0, size = 10 } = {}) {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  if (search) params.set("search", search);
  if (status && status !== "all") params.set("status", status);
  if (from) params.set("from", from);
  if (to) params.set("to", to);

  const data = await apiRequest(`/returns?${params.toString()}`);
  return {
    rows: Array.isArray(data?.content) ? data.content.map(fromFilingSummary) : [],
    totalElements: data?.totalElements ?? 0,
    totalPages: data?.totalPages ?? 0,
    page: data?.page ?? page,
    size: data?.size ?? size,
  };
}

// DELETE /returns/{id} -> soft-deletes a draft return (backend sets
// deleted_at; it stops appearing in My Filings). 409 if the return isn't a
// draft (new/half/full) - submitted/accepted/rejected returns are filing
// records and can't be deleted.
export async function deleteFiling(returnId) {
  await apiRequest(`/returns/${returnId}`, { method: "DELETE" });
}

// GET /returns/{returnId}/rejection-errors -> the real IRS rejection detail for
// this return (real error codes/messages, classified as user-correctable or
// support-required by the backend). errors is [] both when the return isn't
// rejected and when it is rejected but no parseable IRS acknowledgement is
// available yet - the "View Error" modal tells those apart via status.
export async function getRejectionErrors(returnId) {
  return apiRequest(`/returns/${returnId}/rejection-errors`);
}
